import {
  doc,
  DocumentData,
  getDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase/config";
import type { UserData } from "../types/user";
import { safeRemoveItem } from "../utils/localStorageUtils";

const PUBLIC_FIELDS = [
  "username",
  "reason",
  "cycleDays",
  "trainingSportIDs",
  "blockDays",
  "sleepGoal",
  "loadGoal",
  "weeklyLoadGoal",
  "performanceGoals",
  "healthGoals",
  "campaignCode",
  "competitionLevel",
  "inviteCode",
  "onboardingComplete",
  "onboardingResumeStep",
  "nativeSetupPending",
  "simpleWellnessSetting",
  "casualMode",
  "disableInsights",
  "hidePeakScore",
  "hideWellnessSleep",
  "isImperial",
  "autoPostTraining",
  "isPrivateProfile",
  "deActivated",
  "notifySettings",
  "reminderGmtHour",
] as const;

const PRIVATE_FIELDS = ["email", "dob", "name", "surname", "gender", "country"] as const;

function pick<T extends Record<string, unknown>>(obj: T, fields: readonly string[]) {
  const result: Record<string, unknown> = {};
  fields.forEach((field) => {
    if (obj[field] !== undefined) result[field] = obj[field];
  });
  return result;
}

async function claimUsernameIfChanged(username: unknown, currentUsername: unknown) {
  if (typeof username !== "string" || !username || username === currentUsername) return;
  try {
    await httpsCallable(functions, "claimUsername")({ username });
  } catch (error: unknown) {
    const coded = error as { code?: string; message?: string };
    if (coded.code === "functions/already-exists") throw new Error("Username is already taken");
    if (coded.code === "functions/invalid-argument") throw new Error(coded.message || "Invalid username");
    throw error;
  }
}

export const userService = {
  async getUserData(userId: string): Promise<UserData | null> {
    const publicDoc = await getDoc(doc(db, "users", userId));
    if (!publicDoc.exists()) return null;
    let privateData: UserData = {};
    try {
      const privateDoc = await getDoc(doc(db, "users", userId, "private", "data"));
      if (privateDoc.exists()) privateData = privateDoc.data() as UserData;
    } catch (error) {
      console.warn("Could not read private profile data:", error);
    }
    return { ...(publicDoc.data() as UserData), ...privateData };
  },

  async completeUserRegistration(userId: string, userData: UserData): Promise<void> {
    const publicPayload: DocumentData = pick(userData as Record<string, unknown>, PUBLIC_FIELDS);
    const privatePayload: DocumentData = pick(userData as Record<string, unknown>, PRIVATE_FIELDS);
    delete publicPayload.role;
    delete publicPayload.isCoach;
    delete publicPayload.isEmailVerified;

    const userRef = doc(db, "users", userId);
    const privateRef = doc(db, "users", userId, "private", "data");
    const existing = await getDoc(userRef);
    if (existing.data()?.onboardingComplete === true) return;

    await claimUsernameIfChanged(userData.username, existing.data()?.username);

    if (existing.exists()) {
      await setDoc(userRef, publicPayload, { merge: true });
      await setDoc(privateRef, privatePayload, { merge: true });
      return;
    }

    publicPayload.accountCreatedAt = serverTimestamp();
    await setDoc(userRef, publicPayload);
    await setDoc(privateRef, privatePayload);
  },

  async updateUserProfile(userId: string, updates: Partial<UserData>): Promise<void> {
    if (updates.username !== undefined) {
      const current = await getDoc(doc(db, "users", userId));
      await claimUsernameIfChanged(updates.username, current.data()?.username);
    }
    const publicUpdates = pick(updates as Record<string, unknown>, PUBLIC_FIELDS);
    const privateUpdates = pick(updates as Record<string, unknown>, PRIVATE_FIELDS);
    delete publicUpdates.role;
    delete publicUpdates.isCoach;
    if (Object.keys(publicUpdates).length > 0) {
      await setDoc(doc(db, "users", userId), publicUpdates, { merge: true });
    }
    if (Object.keys(privateUpdates).length > 0) {
      await setDoc(doc(db, "users", userId, "private", "data"), privateUpdates, { merge: true });
    }
  },

  cleanupCampaignCode(campaignCode?: string) {
    if (campaignCode) safeRemoveItem("campaignCode");
  },
};
